import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { useAuth } from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import loginImage from 'assets/img/login/login_image.jpg';
import inputiloginImage from 'assets/img/login/inputi_login_image.jpg';
import TNS_logo from 'assets/img/login/tns_logo.png';
import AMBUJA_logo from 'assets/img/login/ambuja_logo.png';
import SESTA_logo from 'assets/img/login/sesta_logo.png';
import FARMSIO_logo from 'assets/img/login/farmsio_logo.png';
import IDH_logo from 'assets/img/login/idh_logo.png';
import MRV_logo from 'assets/img/login/mrv_logo.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const getDomainLogo = () => {
  const domainName = window.location.hostname.split('.')[0].toLowerCase();

  // Define a mapping of domain names to logos
  const domainLogos = {
    tnsmrv: TNS_logo,
    tns: TNS_logo,
    ambujafoundation: AMBUJA_logo,
    sesta: SESTA_logo,
    farmsio: FARMSIO_logo,
    rplc: IDH_logo,
    mrv: MRV_logo
  };

  // Return the appropriate logo, or default to FARMSIO_logo
  return domainLogos[domainName] || MRV_logo;
};

const getDomainLoginImage = () => {
  const domainName = window.location.hostname.split('.')[0].toLowerCase();

  // For MRV domain, use inputiloginImage; for all others, use loginImage
  return domainName === 'mrv' ? inputiloginImage : loginImage;
};

const LoginPage = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  const { login, isAuthenticated, isOffline, getDefaultRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if there's a specific redirect request in the query parameters
  const queryParams = new URLSearchParams(location.search);
  const requestedRedirect = queryParams.get('redirect');

  useEffect(() => {
    // Only handle auto-redirect if authenticated and not already redirecting
    if (isAuthenticated && !redirectInProgress) {
      setRedirectInProgress(true);

      // Small timeout to ensure tokens and tenant data are fully processed
      setTimeout(() => {
        try {
          // Use the requested redirect if available, otherwise get default route
          // Layout component will handle special paths that need clusters
          const redirectTo = requestedRedirect || getDefaultRoute();

          // console.log(`Redirecting authenticated user to: ${redirectTo}`);
          navigate(redirectTo, { replace: true });
        } catch (err) {
          console.error('Error during redirect:', err);
          // Fallback to root path
          navigate('/', { replace: true });
        }
      }, 300);
    }
  }, [
    isAuthenticated,
    navigate,
    getDefaultRoute,
    requestedRedirect,
    redirectInProgress
  ]);

  useEffect(
    () =>
      setError(
        isOffline
          ? 'You are currently offline. Please connect to the internet to log in.'
          : ''
      ),
    [isOffline]
  );

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = e => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleError = err => {
    if (err.response) {
      const messages = {
        401: 'Invalid username or password',
        429: 'Too many login attempts. Please try again later.'
      };
      setError(
        messages[err.response.status] ||
          `Login failed: ${err.response.data?.message || 'Server error'}`
      );
    } else {
      setError(
        err.request
          ? 'No response from server. Please check your connection.'
          : 'Login failed. Please try again.'
      );
    }
  };

  const handleLogin = async e => {
    e.preventDefault();
    if (isOffline)
      return setError(
        'Cannot log in while offline. Please connect to the internet.'
      );
    setError('');
    setIsLoading(true);

    try {
      await login(credentials.username, credentials.password);
      // Authentication successful
      // The redirection will be handled by the useEffect hook that watches isAuthenticated
    } catch (err) {
      handleError(err);
      setIsLoading(false); // Only reset loading on error
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column flex-lg-row">
      {/* Left side - Image section (6/12 width) */}
      <div className="d-none d-lg-flex col-lg-6 bg-primary">
        <div
          className="w-100 h-100"
          style={{
            backgroundImage: `url(${getDomainLoginImage()})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
      </div>

      {/* Right side - Login form (6/12 width) */}
      <div className="col-12 col-lg-6 d-flex align-items-center justify-content-center">
        <div className="w-100 p-3 p-md-4 p-lg-5" style={{ maxWidth: '500px' }}>
          <div className="mb-0 text-center">
            <img
              src={getDomainLogo()}
              alt="Company Logo"
              className="img-fluid"
              style={{ maxHeight: '160px' }}
            />
          </div>
          <div className="card p-4">
            <div className="text-start mb-2">
              <h3 className="fw-semibold mb-1">Login</h3>
            </div>

            <Form onSubmit={handleLogin}>
              {error && <Alert variant="danger">{error}</Alert>}
              {isOffline && (
                <Alert variant="warning">
                  You are currently offline. Some features may be limited.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  disabled={isLoading || isOffline || redirectInProgress}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    disabled={isLoading || isOffline || redirectInProgress}
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={togglePasswordVisibility}
                    disabled={isLoading || isOffline || redirectInProgress}
                    style={{ border: '1px solid #ced4da' }}
                  >
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <a
                    href="#"
                    className="text-decoration-none small"
                    onClick={e => {
                      e.preventDefault();
                      setShowForgotModal(true);
                    }}
                  >
                    Forgot password?
                  </a>
                </div>
              </Form.Group>

              <Form.Group className="mb-4">
                <Button
                  type="submit"
                  className="w-100"
                  disabled={isLoading || isOffline || redirectInProgress}
                >
                  {isLoading || redirectInProgress ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {redirectInProgress ? 'Redirecting...' : 'Logging in...'}
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
              </Form.Group>
            </Form>
          </div>
        </div>
      </div>
      {/* Forgot Password Modal */}
      <Modal
        show={showForgotModal}
        onHide={() => setShowForgotModal(false)}
        centered
      >
        <Modal.Body className="p-4">
          <h5 className="mb-3 text-start">Forgot Password</h5>
          <p className="mb-4 text-start">
            Please contact your administrator to reset your password.
          </p>
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={() => setShowForgotModal(false)}>
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LoginPage;
